import React, {useCallback, useEffect, useMemo, useRef, useState} from 'react'
import moment from 'moment'
import {useNavigate} from 'react-router-dom'
import {useLayoutPanels} from '@/shared/context/ChatContext'

import {Job, useFetchHistory, formatBytes, getLogger, useStorageUsage, useBalance} from '../../../../shared/utils'
import {useSubscription} from '@/shared/utils'

import {ClientSideRowModelModule} from '@ag-grid-community/client-side-row-model'
import {ModuleRegistry, ProcessRowParams} from '@ag-grid-community/core'
import {AgGridReact} from '@ag-grid-community/react' // the AG Grid React Component
import {MasterDetailModule} from '@ag-grid-enterprise/master-detail'
import {SetFilterModule} from '@ag-grid-enterprise/set-filter'

import '@ag-grid-enterprise/core'
import {LicenseManager} from '@ag-grid-enterprise/core'
import {collapseProps, collapsibleCard} from './external-viewer/collapsible-card'
import './style/ag-theme-metronic.scss'
import ReactTooltip from 'react-tooltip'
import {CustomModal} from './CustomModal'
import {toast} from 'react-toastify'
import {toastDefaultConfig} from '../../../../shared/config/toastConfig'
import {FileTableSkeleton} from './FileTableSkeleton'
import {filterJobsByOmicsAI, loadShowOmicsAI, saveShowOmicsAI} from './jobHistoryFilters'
import {makeJobRowDraggable} from './jobDrag'

import {Auth} from 'aws-amplify'

// WES (Workflow Execution Service) configuration from environment variables
const WES_CONFIG = {
  baseUri:
    process.env.REACT_APP_WES_BASE_URI || 'https://ee66ege16i.execute-api.us-east-2.amazonaws.com',
  basePath: process.env.REACT_APP_WES_BASE_PATH || '/prod',
  apiVersion: process.env.REACT_APP_WES_API_VERSION || '2.0',
}

LicenseManager.setLicenseKey(
  'Using_this_AG_Grid_Enterprise_key_( AG-049664 )_in_excess_of_the_licence_granted_is_not_permitted___Please_report_misuse_to_( legal@ag-grid.com )___For_help_with_changing_this_key_please_contact_( info@ag-grid.com )___( BioBam Bioinformatics S.L. )_is_granted_a_( Single Application )_Developer_License_for_the_application_( OmicsBox Web )_only_for_( 1 )_Front-End_JavaScript_developer___All_Front-End_JavaScript_developers_working_on_( OmicsBox Web )_need_to_be_licensed___( OmicsBox Web )_has_been_granted_a_Deployment_License_Add-on_for_( 1 )_Production_Environment___This_key_works_with_AG_Grid_Enterprise_versions_released_before_( 23 October 2024 )____[v2]_MTcyOTYzODAwMDAwMA==ba072e87b25bddc59335e28e6dfb7736'
)
ModuleRegistry.registerModules([ClientSideRowModelModule, MasterDetailModule, SetFilterModule])

// Custom loading overlay component for AG Grid
const CustomLoadingOverlay = () => {
  return (
    <div 
      style={{
        position: 'absolute',
        top: 0,
        left: 0,
        right: 0,
        bottom: 0,
        backgroundColor: 'var(--ob-loading-overlay-bg, rgba(255, 255, 255, 0.95))',
        display: 'flex',
        alignItems: 'flex-start',
        justifyContent: 'center',
        zIndex: 1000,
        paddingTop: '10px'
      }}
    >
      <div style={{ width: '100%', maxWidth: '100%' }}>
        <FileTableSkeleton rowCount={10} showHeader={true} />
      </div>
    </div>
  )
}

// Helper function from HistoryViewerTable.ts
function humanFileSize(bytes: number, si = false, dp = 1) {
  if (typeof bytes !== 'number' || bytes === null || isNaN(bytes)) {
    return '--'
  }
  const thresh = si ? 1000 : 1024

  if (Math.abs(bytes) < thresh) {
    return bytes + ' B'
  }

  const units = si
    ? ['kB', 'MB', 'GB', 'TB', 'PB', 'EB', 'ZB', 'YB']
    : ['KiB', 'MiB', 'GiB', 'TiB', 'PiB', 'EiB', 'ZiB', 'YiB']
  let u = -1
  const r = 10 ** dp

  do {
    bytes /= thresh
    ++u
  } while (Math.round(Math.abs(bytes) * r) / r >= thresh && u < units.length - 1)

  return bytes.toFixed(dp) + ' ' + units[u]
}

// Helper functions adapted from HistoryViewerTable.ts
const getJobStatus = (params: any) => {
  // Check if this is the new data model (has runState property)
  if (params.data.runState !== undefined) {
    return params.data.runState
  }

  // Old data model - determine state from job counts
  const data = params.data
  const countJobsStart = data.countJobsStart || 0
  const countJobsDone = data.countJobsDone || 0
  const countJobsCancel = data.countJobsCancel || 0
  const countJobsError = data.countJobsError || 0

  // If there are errors, return ERROR
  if (countJobsError > 0) {
    return 'ERROR'
  }

  // If there are canceled jobs, return CANCELED
  if (countJobsCancel > 0) {
    return 'CANCELED'
  }

  // If all started jobs are done, return DONE
  if (countJobsDone >= countJobsStart) {
    return 'DONE'
  }

  // If no jobs have started, return RUNNING (initial state)
  if (countJobsStart === 0) {
    return 'RUNNING'
  }

  // Otherwise, still running
  return 'RUNNING'
}

const isJobFinished = (params: any) => {
  const status = getJobStatus(params)
  return (
    status === 'COMPLETE' ||
    status === 'DONE' ||
    status === 'CANCELED' ||
    status === 'CANCELING' ||
    status === 'PREEMPTED' ||
    status === 'EXECUTOR_ERROR' ||
    status === 'SYSTEM_ERROR'
  )
}

// Options renderer with action callbacks
const OptionsRenderer = (params: any) => {
  const isWorkflow = params.data.sessionType === 'workflow'
  const hasCloudFiles =
    params.data.s3OutputUri &&
    !params.data.s3OutputUri.startsWith('s3://scloud-temp-workflow-production')
  const canCancel = isWorkflow && !isJobFinished(params)

  // Get callbacks from params (passed from column definition)
  const {onCancel, onViewLogs, onViewMessages, onViewDetails, onViewData} =
    params.colDef.cellRendererParams || {}

  return (
    <div style={{display: 'flex', gap: '10px', alignItems: 'center', height: '100%'}}>
      {canCancel && (
        <i
          className='fa-solid fa-stop'
          title='Cancel'
          style={{cursor: 'pointer', color: 'red'}}
          onClick={() => onCancel?.(params.data)}
        ></i>
      )}
      {isWorkflow && (
        <i
          className='fa-regular fa-file'
          title='Logs'
          style={{cursor: 'pointer'}}
          onClick={() => onViewLogs?.(params.data)}
        ></i>
      )}
      {isWorkflow && (
        <i
          className='fa-regular fa-file-lines'
          title='Messages'
          style={{cursor: 'pointer'}}
          onClick={() => onViewMessages?.(params.data)}
        ></i>
      )}
      {isWorkflow && (
        <i
          className='fa-regular fa-eye'
          title='Details'
          style={{cursor: 'pointer'}}
          onClick={() => onViewDetails?.(params.data)}
        ></i>
      )}
      {hasCloudFiles && (
        <i
          className='fa-regular fa-folder'
          title='Data'
          style={{cursor: 'pointer'}}
          onClick={() => onViewData?.(params.data)}
        ></i>
      )}
    </div>
  )
}

interface TableHistoryAgProps extends collapseProps {
  onViewDetails?: (job: any) => void
  onViewLogs?: (job: any) => void
  onViewMessages?: (job: any) => void
}

function TableHistoryAg(props: TableHistoryAgProps) {
  const todayDate = moment().format('yyyy-MM-DD')
  const backDate = moment().subtract(1, 'years').format('yyyy-MM-DD')
  const navigate = useNavigate()
  const {openFilesPanelAt, expandCenter} = useLayoutPanels()
  const {subscriptionId} = useSubscription()

  // Cancel confirmation modal state
  const [showCancelModal, setShowCancelModal] = useState(false)
  const [analysisToCancel, setAnalysisToCancel] = useState<any>(null)
  const [isCanceling, setIsCanceling] = useState(false)

  const usageQuery = useStorageUsage()
  const balanceQuery = useBalance(subscriptionId)
  const usage = usageQuery.data ?? 0
  const balance = balanceQuery.data ?? 0

  // Callback methods for option actions
  const handleCancelJob = useCallback((jobData: any) => {
    setAnalysisToCancel(jobData)
    setShowCancelModal(true)
  }, [])

  // Note: confirmCancelJob will be defined after historyQuery to avoid scope issues

  // Close cancel confirmation modal
  const closeCancelModal = useCallback(() => {
    setShowCancelModal(false)
    setAnalysisToCancel(null)
    setIsCanceling(false)
  }, [])

  const {onViewDetails, onViewLogs, onViewMessages} = props

  const handleViewLogs = useCallback(
    (jobData: any) => {
      onViewLogs?.(jobData)
    },
    [onViewLogs]
  )

  const handleViewMessages = useCallback(
    (jobData: any) => {
      onViewMessages?.(jobData)
    },
    [onViewMessages]
  )
  const handleViewDetails = useCallback(
    (jobData: any) => {
      onViewDetails?.(jobData)
    },
    [onViewDetails]
  )

  const handleViewData = useCallback(
    (jobData: any) => {
      const s3OutputUri = jobData.s3OutputUri
      if (!s3OutputUri) {
        getLogger().error('No s3OutputUri found in job data')
        return
      }

      try {
        // Parse S3 URI: s3://bucket-name/identity-id/actual/path/
        const uriWithoutProtocol = s3OutputUri.replace(/^s3:\/\//, '')
        const parts = uriWithoutProtocol.split('/').filter((part: string) => part !== '')

        if (parts.length < 2) {
          getLogger().error('Invalid S3 URI format:', s3OutputUri)
          return
        }

        // Remove bucket name (first part) and identity ID (second part)
        const pathParts = parts.slice(2)

        if (pathParts.length === 0) {
          getLogger().error('No path found in S3 URI:', s3OutputUri)
          return
        }

        const relativePath = pathParts.join('/')
        // Files is a lateral panel now, not a route: open it at the job's output folder and put the
        // user on Results, so the panel and their viewers are visible side by side.
        openFilesPanelAt(relativePath)
        expandCenter()
        navigate('/results')
      } catch (error) {
        getLogger().error('Error processing S3 URI:', error)
      }
    },
    [navigate, openFilesPanelAt, expandCenter]
  )

  // TODO: change it to retrieve full table
  const [filteredData, setFilteredData] = useState({
    startCreationTime: new Date(`${backDate}T00:00:00`).getTime(),
    endCreationTime: new Date(`${todayDate}T23:59:59`).getTime(),
  })
  const historyQuery = useFetchHistory(filteredData)
  const [rangeDate] = useState({
    fromDate: backDate,
    toDate: todayDate,
  })
  const [filtering, setFiltering] = useState(false)

  const gridRef = useRef<AgGridReact>(null) // Optional - for accessing Grid's API
  const [rowData, setRowData] = useState<Job[]>() // Set rowData to Array of Objects, one Object per Row
  const [showOmicsAI, setShowOmicsAI] = useState(loadShowOmicsAI)
  const handleShowOmicsAIChange = useCallback((checked: boolean) => {
    setShowOmicsAI(checked)
    saveShowOmicsAI(checked)
  }, [])
  const visibleRowData = useMemo(
    () => filterJobsByOmicsAI(rowData, showOmicsAI),
    [rowData, showOmicsAI]
  )

  const masterDetail = false

  // Rows drag onto the AI chat composer as "Job: <name>" chips (see ./jobDrag).
  const attachJobRowDrag = useCallback((params: ProcessRowParams) => {
    const job = params.node.data as Job | undefined
    ;[params.eRow, params.ePinnedLeftRow, params.ePinnedRightRow].forEach((row) => {
      if (row) makeJobRowDraggable(row, job)
    })
  }, [])

  // Handle actual analysis cancellation after confirmation
  const confirmCancelJob = useCallback(async () => {
    if (!analysisToCancel) return

    // Set canceling state to show progress
    setIsCanceling(true)

    try {
      // Get current authenticated session for authorization
      const session = await Auth.currentSession()
      const jwtToken = session.getIdToken().getJwtToken()

      // Construct WES cancel endpoint URL
      const sessionId = analysisToCancel.SessionId
      // const cancelUrl = `${WES_CONFIG.baseUri}${WES_CONFIG.basePath}/${WES_CONFIG.apiVersion}/runs/${sessionId}/cancel`
      const cancelUrl = `${WES_CONFIG.baseUri}${WES_CONFIG.basePath}/runs/${sessionId}/cancel`

      // Make WES API call to cancel the analysis
      try {
        const response = await fetch(cancelUrl, {
          method: 'POST',
          headers: {
            Authorization: `Bearer ${jwtToken}`,
            'Content-Type': 'application/json',
          },
        })

        if (!response.ok) {
          const errorText = await response.text()
          throw new Error(`WES API error (${response.status}): ${errorText}`)
        }

        getLogger().info('Cancel request completed successfully')
      } catch (fetchError) {
        // Check if this is a CORS error but the request actually succeeded
        if (fetchError instanceof TypeError && fetchError.message.includes('Failed to fetch')) {
          getLogger().info('CORS error detected, but request likely succeeded on server')
          // Continue with success flow since the server processes the request despite CORS
        } else {
          // Re-throw non-CORS errors
          throw fetchError
        }
      }

      // Show success notification
      toast.success('Analysis has been cancelled successfully', toastDefaultConfig)

      // Close modal and reset all state
      setShowCancelModal(false)
      setAnalysisToCancel(null)
      setIsCanceling(false)

      // Refresh the table to show updated status
      historyQuery.refetch()

      // Optional: Set up polling to verify cancellation after a delay
      setTimeout(() => {
        historyQuery.refetch()
      }, 2000) // Check again in 5 seconds
    } catch (error) {
      getLogger().error('Error cancelling analysis:', error)

      // Show error notification
      const errorMessage = error instanceof Error ? error.message : 'Unknown error occurred'
      toast.error(`Failed to cancel analysis: ${errorMessage}`, toastDefaultConfig)

      // Reset canceling state on error but keep modal open so user can try again
      setIsCanceling(false)
    }
  }, [analysisToCancel, historyQuery])

  // Column definitions adapted from HistoryViewerTable.ts
  const [columnDefs] = useState([
    {
      headerName: 'Status',
      field: 'runState',
      width: 115,
      valueGetter: (params: any) => {
        let value = getJobStatus(params)

        switch (value) {
          case 'RUNNING':
            value = 'RUNNING'
            break
          case 'COMPLETE':
            value = 'DONE'
            break
          case 'CANCELED':
          case 'CANCELING':
          case 'PREEMPTED':
            value = 'CANCELLED'
            break
          case 'QUEUED':
          case 'INITIALIZING':
          case 'PAUSED':
            value = 'WAITING'
            break
          case 'UNKNOWN':
            value = 'SENDING'
            break
          case 'EXECUTOR_ERROR':
          case 'SYSTEM_ERROR':
            value = 'ERROR'
            break
        }

        return value
      },
      cellRenderer: (params: any) => {
        return <span className={`status_Tag statusTag_${params.value}`}>{params.value ?? ''}</span>
      },
    },
    {
      headerName: 'Options',
      cellRenderer: OptionsRenderer,
      cellRendererParams: {
        onCancel: handleCancelJob,
        onViewLogs: handleViewLogs,
        onViewMessages: handleViewMessages,
        onViewDetails: handleViewDetails,
        onViewData: handleViewData,
      },
      colId: 'options',
      width: 130,
      filter: false,
      sortable: false,
    },
    {
      headerName: 'Submitted',
      field: 'CreationTime',
      cellDataType: 'date',
      filter: 'agDateColumnFilter',
      filterParams: {
        // Enable range selection
        buttons: ['reset', 'apply'],
        // Show date range picker
        browserDatePicker: true,
        // Allow selecting date ranges
        filterOptions: ['equals', 'greaterThan', 'lessThan', 'inRange'],
        // Default to range filter
        defaultOption: 'inRange',
        // Format dates in the filter UI
        dateFormatter: (date: Date) => {
          if (!date) return ''
          return date.toLocaleDateString()
        },
        // Parse dates from the filter UI
        comparator: (filterLocalDateAtMidnight: Date, cellValue: Date) => {
          if (!cellValue) return 0
          const cellDate = new Date(cellValue)
          if (cellDate < filterLocalDateAtMidnight) {
            return -1
          } else if (cellDate > filterLocalDateAtMidnight) {
            return 1
          } else {
            return 0
          }
        },
      },
      valueGetter: (params: any) => {
        return params.data.CreationTime ? new Date(params.data.CreationTime) : null
      },
      valueFormatter: (params: any): string => {
        if (!params.value) return ''
        const date = params.value
        const formatted = new Intl.DateTimeFormat('en-GB', {
          day: '2-digit',
          month: '2-digit',
          year: '2-digit',
          hour: '2-digit',
          minute: '2-digit',
          hour12: false,
        }).format(date)
        return formatted
      },
      comparator: (valueA: Date, valueB: Date) => {
        if (!valueA && !valueB) return 0
        if (!valueA) return -1
        if (!valueB) return 1
        return valueA.getTime() - valueB.getTime()
      },
      width: 150,
    },
    {
      headerName: 'ID',
      field: 'customId',
      width: 100,
      filter: 'agTextColumnFilter',
      filterParams: {
        buttons: ['reset', 'apply'],
        filterOptions: ['contains', 'startsWith', 'endsWith', 'equals'],
        defaultOption: 'contains',
        caseSensitive: false,
      },
    },
    {
      headerName: 'Task',
      field: 'serviceName',
      flex: 2,
      minWidth: 100,
      filter: 'agTextColumnFilter',
      filterParams: {
        buttons: ['reset', 'apply'],
        filterOptions: ['contains', 'startsWith', 'endsWith', 'equals'],
        defaultOption: 'contains',
        caseSensitive: false,
      },
    },
    {
      headerName: 'Progress',
      field: 'progress',
      filter: 'agNumberColumnFilter',
      filterParams: {
        buttons: ['reset', 'apply'],
        filterOptions: ['equals', 'greaterThan', 'lessThan', 'inRange'],
        defaultOption: 'equals',
      },
      width: 100,
      valueGetter: (params: any) => {
        const status = getJobStatus(params)

        if (status === 'COMPLETE') {
          return 100
        }

        if (status !== 'RUNNING') return null

        const totalJobs = params.data.countJobsStart
        const completedJobs = params.data.countJobsDone

        if (typeof totalJobs !== 'number' || isNaN(totalJobs) || totalJobs === 0) return null

        const percentage = Math.round((completedJobs / totalJobs) * 100)
        return percentage
      },
      valueFormatter: (params: any) => {
        if (params.value === null || params.value === undefined) return '--'
        return `${params.value}%`
      },
    },
    {
      headerName: 'Runtime',
      filter: 'agNumberColumnFilter',
      filterParams: {
        buttons: ['reset', 'apply'],
        filterOptions: ['equals', 'greaterThan', 'lessThan', 'inRange'],
        defaultOption: 'equals',
      },
      width: 120,
      field: 'runtime',
      valueGetter: (params: any) => {
        const creationTime = new Date(params.data.CreationTime)
        const isFinished = isJobFinished(params)

        let stopTime: Date

        if (isFinished) {
          if (!params.data.lastStopTime) {
            return null
          }
          stopTime = new Date(params.data.lastStopTime)
        } else {
          stopTime = new Date()
        }

        const runtime = stopTime.getTime() - creationTime.getTime()

        if (runtime <= 0) {
          return null
        }

        return runtime
      },
      valueFormatter: (params: any) => {
        if (params.value === null || params.value === undefined) return '--'

        const runtime = params.value
        const runtimeDays = Math.floor(runtime / (1000 * 60 * 60 * 24))
        const runtimeHours = Math.floor((runtime % (1000 * 60 * 60 * 24)) / (1000 * 60 * 60))
        const runtimeMinutes = Math.floor((runtime % (1000 * 60 * 60)) / (1000 * 60))
        const runtimeSeconds = Math.floor((runtime % (1000 * 60)) / 1000)

        return `${runtimeDays > 0 ? `${runtimeDays}d ` : ''}${
          runtimeHours > 0 ? `${runtimeHours}h ` : ''
        } ${runtimeMinutes > 0 ? `${runtimeMinutes}m ` : ''}${
          runtimeSeconds > 0 ? `${runtimeSeconds}s` : ''
        }`
      },
    },
    {
      headerName: 'Input',
      field: 'inputDataSize',
      width: 100,
      cellDataType: 'number',
      filter: 'agNumberColumnFilter',
      filterParams: {
        buttons: ['reset', 'apply'],
        filterOptions: ['equals', 'greaterThan', 'lessThan', 'inRange'],
        defaultOption: 'equals',
      },
      valueGetter: (params: any) => {
        return params.data.inputDataSize || null
      },
      valueFormatter: (params: any) => {
        return humanFileSize(params.value)
      },
      comparator: (valueA: number, valueB: number) => {
        if (valueA === null && valueB === null) return 0
        if (valueA === null) return -1
        if (valueB === null) return 1
        return valueA - valueB
      },
    },
    {
      headerName: 'Output',
      field: 'outputDataSize',
      width: 100,
      cellDataType: 'number',
      filter: 'agNumberColumnFilter',
      filterParams: {
        buttons: ['reset', 'apply'],
        filterOptions: ['equals', 'greaterThan', 'lessThan', 'inRange'],
        defaultOption: 'equals',
      },
      valueGetter: (params: any) => {
        return params.data.outputDataSize || null
      },
      valueFormatter: (params: any) => {
        return humanFileSize(params.value)
      },
      comparator: (valueA: number, valueB: number) => {
        if (valueA === null && valueB === null) return 0
        if (valueA === null) return -1
        if (valueB === null) return 1
        return valueA - valueB
      },
    },
    {
      headerName: 'Consumption',
      field: 'consumedUnits',
      type: 'rightAligned',
      filter: 'agNumberColumnFilter',
      filterParams: {
        buttons: ['reset', 'apply'],
        filterOptions: ['equals', 'greaterThan', 'lessThan', 'inRange'],
        defaultOption: 'equals',
      },
      width: 150,
      valueGetter: (params: any) => {
        return params.data.consumedUnits || null
      },
    },
    {
      headerName: 'Charged',
      field: 'billedUnits',
      type: 'rightAligned',
      filter: 'agNumberColumnFilter',
      filterParams: {
        buttons: ['reset', 'apply'],
        filterOptions: ['equals', 'greaterThan', 'lessThan', 'inRange'],
        defaultOption: 'equals',
      },
      valueGetter: (params: any) => {
        return params.data.billedUnits !== undefined ? params.data.billedUnits : null
      },
      valueFormatter: (params: any): string => {
        if (params.value === null || params.value === undefined) return '--'
        return `${params.value} ${
          params.data.consumedUnits > 0 && params.value === 0 ? ' (included)' : ''
        }`
      },
      width: 130,
    },
  ])

  const detailCellRendererParams = useMemo(() => {
    return {
      detailGridOptions: {
        columnDefs: [{field: 'Parameter'}, {field: 'Value'}],
        defaultColDef: {
          flex: 1,
          resizable: false,
        },
      },
      getDetailRowData: (params: any) => {
        const paramDetails = Object.entries(JSON.parse(params.data.parameters)).map(
          ([key, value]: [any, any]) => ({Parameter: key, Value: value})
        )

        params.successCallback(paramDetails)
      },
    }
  }, [])

  // DefaultColDef sets props common to all Columns
  const defaultColDef = useMemo(
    () => ({
      width: 150,
      suppressHeaderMenuButton: true,
      suppressHeaderFilterButton: false,
      suppressMovable: true,
      suppressSizeToFit: true,
      suppressMenu: true,
      resizable: false,
      sortable: true,
      filter: true,
    }),
    []
  )

  useEffect(() => {
    if (historyQuery.data) {
      setRowData(historyQuery.data)

      if (gridRef.current?.api) {
        gridRef.current.api.hideOverlay()
      }

      setFiltering(false)
    }
  }, [historyQuery.data])

  const FilterTable = useCallback(() => {
    if (rangeDate.fromDate && rangeDate.toDate) {
      const fromTimestamp = new Date(`${rangeDate.fromDate}T00:00:00`).getTime()
      const toTimestamp = new Date(`${rangeDate.toDate}T23:59:59`).getTime()
      const rangeValid = toTimestamp >= fromTimestamp

      if (
        rangeValid &&
        (filteredData.startCreationTime !== fromTimestamp ||
          filteredData.endCreationTime !== toTimestamp)
      ) {
        setFilteredData({
          startCreationTime: fromTimestamp,
          endCreationTime: toTimestamp,
        })

        if (gridRef.current) {
          gridRef.current.api.showLoadingOverlay()
        }

        historyQuery.refetch()
      }
    }

    setFiltering(false)
  }, [filteredData, historyQuery, rangeDate])

  useEffect(() => {
    if (filtering === true) {
      FilterTable()
    }
  }, [FilterTable, filtering])





  return (
    <div
      className='ag-theme-metronic w-100'
      style={{display: 'flex', flexDirection: 'column', height: '100%'}}
    >
             {/* Header with cloud units on left and total usage on right */}
       <div
         className='card-header border-0 pt-2'
         style={{
           minHeight: '50px',
           paddingTop: 'var(--ob-spacing-2, 0.5rem)',
           paddingBottom: 'var(--ob-spacing-2, 0.5rem)',
           borderBottom: '1px solid var(--ob-file-table-border-color, #dee2e6)',
           display: 'flex',
           justifyContent: 'space-between',
           alignItems: 'center',
         }}
       >
         {/* Cloud Units Display - Left Side */}
         <div
           style={{
             color: 'var(--kt-text-muted)',
             fontSize: 'var(--ob-font-size-sm)',
             fontWeight: '500',
             display: 'flex',
             alignItems: 'center',
             gap: 'var(--ob-spacing-1)',
           }}
         >
           <i className='fas fa-cloud' style={{fontSize: 'var(--ob-font-size-sm)'}}></i>
           <span>User Available: {balance.toLocaleString()} units</span>
         </div>

         {/* Total Usage and Refresh - Right Side */}
         <div
           style={{
             color: 'var(--kt-text-muted)',
             fontSize: 'var(--ob-font-size-sm)',
             fontWeight: '500',
             display: 'flex',
             alignItems: 'center',
             gap: 'var(--ob-spacing-2)',
           }}
         >
           <div
             className='form-check form-switch form-switch-sm form-check-custom form-check-solid'
             style={{marginRight: 'var(--ob-spacing-2)'}}
           >
             <input
               className='form-check-input'
               type='checkbox'
               id='job-history-show-omicsai'
               checked={showOmicsAI}
               onChange={(e) => handleShowOmicsAIChange(e.target.checked)}
             />
             <label
               className='form-check-label'
               htmlFor='job-history-show-omicsai'
               style={{color: 'var(--kt-text-muted)', fontSize: 'var(--ob-font-size-sm)'}}
             >
               Show OmicsAI
             </label>
           </div>
           <div
             style={{
               display: 'flex',
               alignItems: 'center',
               gap: 'var(--ob-spacing-1)',
             }}
           >
             <i className='fas fa-hdd' style={{fontSize: 'var(--ob-font-size-sm)'}}></i>
             <span>Total usage: {formatBytes(usage)}</span>
           </div>
           <button
             data-tip
             data-for='refresh-jobs'
             className='btn btn-sm'
             style={{
               padding: 'var(--ob-spacing-1) var(--ob-spacing-2)',
               display: 'flex',
               alignItems: 'center',
               gap: 'var(--ob-spacing-1)',
               fontSize: 'var(--ob-font-size-sm)',
               fontWeight: '500',
               transition: 'all 0.2s ease',
             }}
             onMouseEnter={(e) => {
               e.currentTarget.style.color = '#dbde8e'
               e.currentTarget.style.borderColor = '#dbde8e'
             }}
             onMouseLeave={(e) => {
               e.currentTarget.style.color = ''
               e.currentTarget.style.borderColor = ''
             }}
             onClick={() => {
               if (gridRef.current?.api) {
                 gridRef.current.api.showLoadingOverlay()
               }
               historyQuery.refetch()
             }}
           >
             <i className='fas fa-sync-alt svg-icon-1 w-16px h-16px' aria-hidden='true'></i>
             <span>Refresh</span>
           </button>
           <ReactTooltip id='refresh-jobs' effect='solid'>
             <span>Refresh job list</span>
           </ReactTooltip>
         </div>
       </div>

      <div style={{flex: 1, overflow: 'auto', minHeight: 0, margin: '0 1rem'}}>
        <div className='table-responsive' style={{display: 'unset', height: '100%'}}>
                      <AgGridReact
              ref={gridRef}
              masterDetail={masterDetail}
              rowData={visibleRowData} // Row Data for Rows
              columnDefs={columnDefs} // Column Defs for Columns
              defaultColDef={defaultColDef} // Default Column Properties
              animateRows={true} // Optional - set to 'true' to have rows animate when sorted
              rowSelection='multiple' // Options - allows click selection of rows
              detailCellRendererParams={detailCellRendererParams}
              tooltipShowDelay={500}
              getRowId={(params: any) => params.data.SessionId}
              processRowPostCreate={attachJobRowDrag}
              suppressContextMenu={true}
              suppressColumnVirtualisation={true}
              rowHeight={30}
              loadingOverlayComponent={CustomLoadingOverlay}
            />
        </div>
      </div>

      {/* Cancel confirmation modal */}
      {showCancelModal && analysisToCancel && (
        <CustomModal
          key={'cancel-analysis-modal'}
          closeModal={closeCancelModal}
          title={isCanceling ? 'Canceling Analysis' : 'Cancel Analysis'}
          buttons={
            isCanceling
              ? [
                  {
                    text: 'Close',
                    className: 'btn btn-secondary',
                    onClick: closeCancelModal,
                  },
                ]
              : [
                  {
                    text: 'Cancel',
                    className: 'btn btn-light',
                    onClick: closeCancelModal,
                  },
                  {
                    text: 'Confirm',
                    className: 'btn btn-danger',
                    onClick: confirmCancelJob,
                  },
                ]
          }
        >
          <div style={{padding: '20px 0', textAlign: 'center'}}>
            {isCanceling ? (
              <div
                style={{
                  display: 'flex',
                  flexDirection: 'column',
                  alignItems: 'center',
                  justifyContent: 'center',
                  padding: '20px',
                }}
              >
                <div className='spinner-border text-primary mb-3' role='status'>
                  <span className='visually-hidden'>Canceling...</span>
                </div>
                <div style={{fontSize: 'var(--ob-font-size-lg)', color: 'var(--ob-text-secondary)'}}>Canceling analysis...</div>
                <div style={{fontSize: 'var(--ob-font-size-base)', color: 'var(--ob-text-muted)', marginTop: '8px'}}>
                  Please wait while we process your cancellation request
                </div>
              </div>
            ) : (
              <div style={{fontSize: 'var(--ob-font-size-lg)'}}>Are you sure you want to cancel this analysis?</div>
            )}
          </div>
        </CustomModal>
      )}
    </div>
  )
}

export default collapsibleCard(TableHistoryAg)
